import { useCallback, useEffect } from 'react';
import { UseInfiniteDocumentScrollProps } from './props';
import { debounce } from 'lodash';

/**
 * Custom hook to implement infinite scrolling functionality.
 * It listens to the scroll event and triggers a callback to load more content
 * when the user scrolls near the bottom of the page.
 *
 * @param {UseInfiniteDocumentScrollProps} props - The properties for the infinite scroll hook.
 * @param {boolean} props.hasMore - Indicates if there are more items to load.
 * @param {boolean} props.isLoading - Indicates if a loading operation is currently in progress.
 * @param {Function} props.loadMore - The function to call to load more items.
 * @param {boolean} [props.condition=true] - An additional condition to check before loading more items.
 *
 * Example usage:
 * useInfiniteDocumentScroll({
 *   hasMore: true,
 *   isLoading: false,
 *   loadMore: () => fetchMoreItems(),
 *   condition: true,
 * });
 */
export function useInfiniteDocumentScroll({
  hasMore,
  isLoading,
  loadMore,
  condition = true,
}: UseInfiniteDocumentScrollProps) {
  /**
   * Memoized scroll event listener that checks if more content should be loaded.
   * It uses lodash's debounce to limit the rate at which the function is called.
   */
  const scrollListener = useCallback(
    debounce(() => {
      // Determine if more content should be loaded based on scroll position and conditions
      const shouldLoadMore =
        window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 600 &&
        hasMore &&
        !isLoading &&
        condition;

      // If conditions are met, call the loadMore function
      if (shouldLoadMore) {
        loadMore();
      }
    }, 100),
    [hasMore, loadMore, isLoading, condition], // Dependencies for useCallback
  );

  /**
   * Effect hook to add the scroll event listener when the component mounts
   * and remove it when the component unmounts.
   */
  useEffect(() => {
    if (window) {
      // Add scroll event listener
      window.addEventListener('scroll', scrollListener);

      // Cleanup function to remove the event listener
      return () => {
        window.removeEventListener('scroll', scrollListener);
      };
    }
  }, [scrollListener]); // Dependency array for useEffect
}

export type { UseInfiniteDocumentScrollProps };
